import {
  createContext,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react'
import type {
  ComponentProps,
  JSX,
  MouseEvent,
  ReactElement,
  ReactNode,
  RefObject,
} from 'react'
import { Link } from 'react-router-dom'
import type { IconButtonProps } from '@buffer-mono/popcorn'

import { ExpandIcon, IconButton, Popover, Tooltip } from '@buffer-mono/popcorn'
import { PortalContainerProvider } from '@buffer-mono/popcorn/src/helpers/usePortalContainer'

import {
  NotesDismissGuardProvider,
  useNotesDismissGuard,
} from '~publish/hooks/notes/NotesDismissGuard'

import styles from './NotesPopover.module.css'

const isNotesTriggerTarget = (
  target: EventTarget | null,
  trigger: HTMLElement | null,
): boolean => target instanceof Node && Boolean(trigger?.contains(target))

export type NotesPopoverTriggerProps = Pick<
  IconButtonProps,
  'variant' | 'size' | 'className'
>

type NotesPopoverContextValue = {
  /** Labels the popover; `NotesPopoverTitle` puts it on the heading. */
  headingId: string
  close: () => void
  /** The popover's body, which takes focus when nothing in it can. */
  rootRef: RefObject<HTMLDivElement | null>
}

const NotesPopoverContext = createContext<NotesPopoverContextValue | null>(null)

const useNotesPopover = (): NotesPopoverContextValue => {
  const context = useContext(NotesPopoverContext)
  if (!context) throw new Error('Use inside NotesPopover')
  return context
}

/**
 * The popover's body, to focus when the thread has nothing else to focus,
 * such as after deleting the last Note without a composer.
 */
export const useNotesPopoverRootRef = (): RefObject<HTMLDivElement | null> =>
  useNotesPopover().rootRef

/**
 * The Notes popover on a card: an icon trigger that opens a Notes thread
 * beside it. The caller supplies the trigger's icon and label, and composes
 * the thread from `NotesPopoverHeader` and its own feed; this owns opening
 * and closing. The children mount only while the popover is open.
 *
 * A Note being typed holds the popover open: closing it by Escape or an
 * outside click asks first, through its own dismiss guard. The guard is the
 * popover's alone, so a draft in another Notes surface never decides it.
 */
export function NotesPopover(props: NotesPopoverProps): JSX.Element {
  return (
    <NotesDismissGuardProvider>
      <NotesPopoverContent {...props} />
    </NotesDismissGuardProvider>
  )
}

type NotesPopoverProps = NotesPopoverTriggerProps & {
  /** The trigger's accessible name. */
  label: string
  /** The trigger's tooltip, hidden while the popover is open. */
  tooltip: ReactNode
  /** The trigger's content, usually a `NotesIndicator` around the icon. */
  icon: ReactElement
  onOpen?: () => void
  'data-testid'?: string
  /** Marks a trigger for a subject with no Notes, so the host can hide it until hover. */
  'data-notes-empty'?: boolean
  children: ReactNode
}

function NotesPopoverContent({
  variant,
  size,
  className,
  label,
  tooltip,
  icon,
  onOpen,
  'data-testid': testId,
  'data-notes-empty': notesEmpty,
  children,
}: NotesPopoverProps): JSX.Element {
  const guard = useNotesDismissGuard()
  const headingId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const pointerDownRef = useRef(false)
  const [open, setOpen] = useState(false)
  const [tooltipOpen, setTooltipOpen] = useState(false)
  const context = useMemo(
    () => ({ headingId, close: (): void => setOpen(false), rootRef }),
    [headingId],
  )
  useEffect(() => {
    if (!open) return
    const down = (): void => {
      pointerDownRef.current = true
    }
    const up = (): void => {
      pointerDownRef.current = false
    }
    document.addEventListener('pointerdown', down, true)
    document.addEventListener('pointerup', up, true)
    document.addEventListener('pointercancel', up, true)
    return (): void => {
      document.removeEventListener('pointerdown', down, true)
      document.removeEventListener('pointerup', up, true)
      document.removeEventListener('pointercancel', up, true)
    }
  }, [open])

  const onOpenChange = (next: boolean): void => {
    if (!next && guard.interceptClose()) return
    setOpen(next)
    if (next) onOpen?.()
  }

  const main =
    typeof document === 'undefined' ? null : document.getElementById('main')
  const overlayContainer =
    typeof document === 'undefined' ? null : document.body

  return (
    <PortalContainerProvider container={main}>
      <Popover open={open} onOpenChange={onOpenChange}>
        <Tooltip
          content={tooltip}
          // Always controlled, so Radix never warns about the switch; the
          // tooltip stays hidden while the popover it describes is open.
          open={tooltipOpen && !open}
          onOpenChange={setTooltipOpen}
        >
          <Popover.Trigger>
            <IconButton
              ref={triggerRef}
              className={className}
              data-notes-empty={notesEmpty || undefined}
              label={label}
              size={size}
              type="button"
              variant={variant}
            >
              {icon}
            </IconButton>
          </Popover.Trigger>
        </Tooltip>
        <Popover.Content
          align="start"
          alignOffset={18}
          sideOffset={-16}
          aria-labelledby={headingId}
          className={styles.popover}
          data-testid={testId}
          onEscapeKeyDown={guard.onEscapeKeyDown}
          onFocusOutside={(event) => {
            // Clicking a button focuses it before Radix reports the pointer
            // outside (that event waits for click). Treating the focus move as
            // its own dismiss spends the warning, and the pointer event then
            // closes the popover.
            if (
              pointerDownRef.current ||
              isNotesTriggerTarget(event.target, triggerRef.current)
            ) {
              event.preventDefault()
            }
          }}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onPointerDownOutside={(event) => {
            // The trigger is outside the content, so this fires on the same
            // click that toggles the popover. Counting it as an outside
            // dismiss runs the unsaved guard twice and clears the warning.
            if (isNotesTriggerTarget(event.target, triggerRef.current)) {
              event.preventDefault()
              return
            }
            guard.onPointerDownOutside(event)
          }}
          side="right"
        >
          <PortalContainerProvider container={overlayContainer}>
            <NotesPopoverContext.Provider value={context}>
              <div ref={rootRef} tabIndex={-1} className={styles.notes}>
                {children}
              </div>
            </NotesPopoverContext.Provider>
          </PortalContainerProvider>
        </Popover.Content>
      </Popover>
    </PortalContainerProvider>
  )
}

/** The popover's title row: `NotesPopoverTitle`, then `NotesPopoverActions`. */
export function NotesPopoverHeader({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return <header className={styles.header}>{children}</header>
}

/** "Notes", labelling the popover, followed by its badges. */
export function NotesPopoverTitle({
  children,
}: {
  children?: ReactNode
}): JSX.Element {
  const { headingId } = useNotesPopover()
  return (
    <div className={styles.titleBlock}>
      <h2 id={headingId} className={styles.title}>
        Notes
      </h2>
      {children}
    </div>
  )
}

export function NotesPopoverActions({
  children,
}: {
  children: ReactNode
}): JSX.Element {
  return <div className={styles.headerActions}>{children}</div>
}

/**
 * Closes the popover for the thread's full view: a link when that view has a
 * route, a button that runs `onClick` when the card opens it in place.
 *
 * Leaving drops the popover's composer, so an unsent Note holds the handoff
 * behind the same warning as any other close; a second press goes through.
 */
export function NotesPopoverExpandButton(
  props:
    | { to: ComponentProps<typeof Link>['to']; onClick?: never }
    | { onClick: () => void; to?: never },
): JSX.Element {
  const { close } = useNotesPopover()
  const guard = useNotesDismissGuard()
  const common = {
    label: 'Expand notes',
    tooltip: 'Expand notes',
    size: 'small',
    variant: 'tertiary',
  } as const
  if (props.to !== undefined) {
    return (
      <IconButton
        {...common}
        as={Link}
        to={props.to}
        onClick={(event: MouseEvent): void => {
          if (guard.interceptClose()) {
            event.preventDefault()
            return
          }
          close()
        }}
      >
        <ExpandIcon />
      </IconButton>
    )
  }
  return (
    <IconButton
      {...common}
      type="button"
      onClick={(): void => {
        if (guard.interceptClose()) return
        close()
        props.onClick()
      }}
    >
      <ExpandIcon />
    </IconButton>
  )
}
